import {
  useGenericRefinement,
  BaseRefinementItem,
  RefinementConfig,
  RawRefinementItem,
} from '../../Algolia/useGenericRefinement';

export const BLOOMS_MAP = {
  1: 'Remember',
  2: 'Understand',
  3: 'Apply',
  4: 'Analyze',
  5: 'Evaluate',
  6: 'Create',
} as const;

// Extended mapping to handle variants in Algolia data
export const BLOOMS_VARIANTS_MAP: Record<string, number> = {
  // Standard forms
  Remember: 1,
  Understand: 2,
  Apply: 3,
  Analyze: 4,
  Evaluate: 5,
  Create: 6,
};

export interface BloomsRefinementItem extends BaseRefinementItem {
  bloomsLevel: number;
  bloomsName: string;
}

export interface BloomsMapping {
  levelMap: typeof BLOOMS_MAP;
  variantsMap: typeof BLOOMS_VARIANTS_MAP;
}

export interface BloomsRefinementResult {
  items: BloomsRefinementItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getCountForLevel: (level: number) => number;
  getTotalCount: () => number;
}

// Configuration for Blooms refinement
const bloomsConfig: RefinementConfig<BloomsRefinementItem, BloomsMapping> = {
  attribute: 'Blooms Taxonomy',
  mapping: {
    levelMap: BLOOMS_MAP,
    variantsMap: BLOOMS_VARIANTS_MAP,
  },

  validateItem: (item: RawRefinementItem, mapping: BloomsMapping): boolean => {
    // Try numeric first
    const numericLevel = parseInt(item.value, 10);
    if (!Number.isNaN(numericLevel) && numericLevel >= 1 && numericLevel <= 6) {
      return true;
    }

    // Try text value using variants map
    const textLevel = mapping.variantsMap[item.value];
    return !!textLevel;
  },

  transformItem: (
    item: RawRefinementItem,
    mapping: BloomsMapping,
  ): BloomsRefinementItem => {
    let level: number;

    const numericLevel = parseInt(item.value, 10);
    if (!Number.isNaN(numericLevel)) {
      level = numericLevel;
    } else {
      level = mapping.variantsMap[item.value] || 0;
    }

    const name = mapping.levelMap[level as keyof typeof mapping.levelMap] || 'Unknown';

    return {
      label: name,
      value: level.toString(),
      count: item.count,
      isRefined: item.isRefined,
      bloomsLevel: level,
      bloomsName: name,
    };
  },

  sortItems: (a: BloomsRefinementItem, b: BloomsRefinementItem): number =>
    a.bloomsLevel - b.bloomsLevel,

  groupItems: (items: BloomsRefinementItem[]): BloomsRefinementItem[] => {
    // Group items by Blooms level and combine counts
    const levelGroups: Record<number, BloomsRefinementItem[]> = {};

    items.forEach((item) => {
      if (!levelGroups[item.bloomsLevel]) {
        levelGroups[item.bloomsLevel] = [];
      }
      levelGroups[item.bloomsLevel].push(item);
    });

    // Convert groups to final format, combining counts
    return Object.entries(levelGroups)
      .map(([level, groupItems]) => {
        const totalCount = groupItems.reduce((sum, item) => sum + item.count, 0);
        const isRefined = groupItems.some((item) => item.isRefined);
        const firstItem = groupItems[0];

        return {
          label: firstItem.bloomsName,
          value: level,
          count: totalCount,
          isRefined,
          bloomsLevel: parseInt(level, 10),
          bloomsName: firstItem.bloomsName,
        };
      })
      .sort((a, b) => a.bloomsLevel - b.bloomsLevel);
  },

  getters: {
    getCountForLevel: (items: BloomsRefinementItem[], level: number): number => {
      const item = items.find((bloomItem) => bloomItem.bloomsLevel === level);
      return item?.count || 0;
    },
  },
};

/**
 * Custom hook for Blooms Taxonomy refinements
 * Provides Algolia facet data specifically formatted for Blooms levels
 */
export const useBloomsRefinement = (
  attribute = 'Blooms Taxonomy',
): BloomsRefinementResult => {
  const result = useGenericRefinement<BloomsRefinementItem, BloomsMapping>({
    ...bloomsConfig,
    attribute,
  });

  return {
    ...result,
    getCountForLevel: (level: number) =>
      bloomsConfig.getters?.getCountForLevel(result.items, level) ?? 0,
    getTotalCount: () => result.items.reduce((sum, item) => sum + item.count, 0),
  };
};
